/**
 * /terms/decide?token=... (OCP-657) - the landing page of the accept / reject
 * links in the Terms change notice (AGB V4 13.3). Public: the token
 * identifies the account, the version (in the recipient's language) and the
 * decision the link carries; the page shows the text and asks for one
 * confirmation click. A rejection terminates the account's subscriptions at
 * the next possible date; an acceptance before that date reactivates them.
 *
 * OCP-847 - the same page for an AVV change (data processing agreement,
 * Art. 28 GDPR): the version's `kind` picks the AVV wording.
 *
 * OCP-856 - an acceptance needs one ticked checkbox per open document, each
 * with its served statement: the linked document, and for an AGB acceptance
 * while the AVV in force is open also the AVV (a separate agreement, linked
 * in exactly the version shown). The confirm button stays disabled until
 * every box is ticked; `accept_avv: true` is sent only with the AVV box ticked.
 */
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { OrbotoLockup } from '../components/OrbotoLockup';
import { applyLocale, isPortalLocale } from '../i18n';
import { api, ApiError } from '../lib/api';
import { fmtDate } from '../lib/format-time';
import { termsTextHref } from '../components/TermsAcceptanceGate';
import type { TermsVersionSummary } from './TermsPage';
import { LegalFooter } from '../components/LegalFooter';

interface DecidePreview {
  version: TermsVersionSummary & { content_md: string };
  decision: 'accepted' | 'rejected';
  already: 'accepted' | 'rejected' | null;
  account_label: string | null;
  /** OCP-856 - the wording of the linked document's checkbox (acceptances only). */
  statement?: string | null;
  /** OCP-856 - the AVV open next to an AGB acceptance, and its own checkbox wording. */
  avv_companion?: Pick<TermsVersionSummary, 'id' | 'version' | 'title'> | null;
  avv_statement?: string;
}

interface DecideResult {
  state: string;
  termination: { ends_at: string | null } | null;
}

export function TermsDecidePage() {
  const { t } = useTranslation('terms-decide');
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [preview, setPreview] = useState<DecidePreview | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'invalid' | 'expired' | 'gone' | 'error'>('loading');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<DecideResult | null>(null);
  const [failed, setFailed] = useState<false | 'generic' | 'avv' | 'unpublished'>(false);
  // OCP-856 - one checkbox per open document (acceptances only)
  const [ticked, setTicked] = useState(false);
  const [avvTicked, setAvvTicked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setState('invalid');
        return;
      }
      try {
        const r = await api<DecidePreview>(`/v1/portal/terms/decide?token=${encodeURIComponent(token)}`);
        if (cancelled) return;
        // the link's language is the one the notice was written in
        if (isPortalLocale(r.version.language)) void applyLocale(r.version.language);
        setPreview(r);
        setState('ok');
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 410) setState('gone');
        else if (err instanceof ApiError && err.status === 400 && err.code === 'token_expired') setState('expired');
        else if (err instanceof ApiError && err.status === 400) setState('invalid');
        else setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function confirm(): Promise<void> {
    setBusy(true);
    setFailed(false);
    try {
      const avvShown = preview?.decision === 'accepted' ? (preview.avv_companion ?? null) : null;
      const r = await api<DecideResult>('/v1/portal/terms/decide-by-token', {
        method: 'POST',
        body: { token, ...(avvShown && avvTicked ? { avv_version_id: avvShown.id, accept_avv: true } : {}) },
      });
      setDone(r);
    } catch (err) {
      // OCP-847 - an AGB acceptance while an AVV is in force that the account has
      // not accepted is refused: this page never showed the AVV.
      // No AVV (or AGB) published: the link cannot record anything right now (409).
      setFailed(
        err instanceof ApiError && err.code === 'avv_acceptance_required'
          ? 'avv'
          : err instanceof ApiError && (err.code === 'avv_not_published' || err.code === 'terms_not_published')
            ? 'unpublished'
            : 'generic',
      );
    } finally {
      setBusy(false);
    }
  }

  const v = preview?.version;
  // OCP-847 - i18next context: `title_avv`, `acceptIntro_avv`, ... for an AVV change.
  const ctx = v?.kind === 'avv' ? { context: 'avv' } : {};
  // the AVV is a separate, mandatory agreement; its rejection ends the contract like an AGB rejection (counsel 2026-10-13)
  const rejectCtx = ctx;
  const accepting = preview?.decision === 'accepted';
  const companion = accepting ? (preview?.avv_companion ?? null) : null;
  const allTicked = !accepting || (ticked && (!companion || avvTicked));
  return (
    <div className="min-h-dvh bg-gray-50 dark:bg-gray-800 px-4 py-10 text-gray-900 dark:text-gray-100">
      <div className="mx-auto w-full max-w-3xl rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-sm">
        <div className="mb-6 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <OrbotoLockup className="h-6 w-auto" />
          </Link>
        </div>
        <h1 className="text-xl font-semibold">{t('title', ctx)}</h1>
        {state === 'loading' && <p className="mt-3 text-sm text-gray-500">{t('loading')}</p>}
        {state === 'invalid' && <p className="mt-3 text-sm text-red-700 dark:text-red-300" data-testid="terms-decide-invalid">{t('invalid')}</p>}
        {state === 'expired' && <p className="mt-3 text-sm text-red-700 dark:text-red-300">{t('expired')}</p>}
        {state === 'gone' && <p className="mt-3 text-sm text-amber-800 dark:text-amber-300">{t('gone')}</p>}
        {state === 'error' && <p className="mt-3 text-sm text-red-700 dark:text-red-300">{t('error')}</p>}
        {state === 'ok' && preview && v && (
          <>
            <p className="mt-1 text-sm text-gray-500" data-testid="terms-decide-version">
              {t('version', { version: v.version, date: fmtDate(v.effective_from) })}
              {preview.account_label ? ` · ${t('forAccount', { account: preview.account_label })}` : ''}
            </p>
            {done ? (
              <div className="mt-4 rounded border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 text-sm text-emerald-900 dark:text-emerald-200" data-testid="terms-decide-done">
                {preview.decision === 'accepted'
                  ? t('doneAccepted')
                  : t('doneRejected', { ...rejectCtx, ends: done.termination?.ends_at ? t('endsAt', { date: fmtDate(done.termination.ends_at) }) : '' })}
                <p className="mt-2">
                  <Link to="/" className="underline">
                    {t('portal')}
                  </Link>
                </p>
              </div>
            ) : (
              <div className="mt-4 rounded border border-gray-200 dark:border-gray-800 p-4 text-sm" data-testid="terms-decide-form">
                {preview.already && <p className="mb-2 text-amber-800 dark:text-amber-300">{t(`already_${preview.already}`, preview.already === 'rejected' ? rejectCtx : {})}</p>}
                <p>{preview.decision === 'accepted' ? t('acceptIntro', ctx) : t('rejectIntro', rejectCtx)}</p>
                <p className="mt-1 text-xs text-gray-500">{t('readFull')}</p>
                {accepting && (
                  <label className="mt-3 flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={ticked}
                      onChange={(e) => setTicked(e.target.checked)}
                      disabled={busy}
                      data-testid="terms-decide-accept-checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-700"
                    />
                    <span>{preview.statement ?? t('acceptIntro', ctx)}</span>
                  </label>
                )}
                {companion && (
                  <div className="mt-3 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3" data-testid="terms-decide-avv">
                    <div className="font-medium">{t('avvCompanion', { title: companion.title, version: companion.version })}</div>
                    <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">{t('avvSeparate')}</p>
                    <a href={termsTextHref({ kind: 'avv', version: companion.version })} target="_blank" rel="noopener" className="mt-1 inline-block text-xs text-brand-500 underline" data-testid="terms-decide-avv-link">
                      {t('avvRead')}
                    </a>
                    <label className="mt-2 flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={avvTicked}
                        onChange={(e) => setAvvTicked(e.target.checked)}
                        disabled={busy}
                        data-testid="terms-decide-avv-checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-700"
                      />
                      <span>{preview.avv_statement}</span>
                    </label>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => void confirm()}
                  disabled={busy || !allTicked}
                  data-testid="terms-decide-confirm"
                  className={`mt-3 rounded px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${preview.decision === 'accepted' ? 'bg-brand-500 hover:bg-brand-600' : 'bg-red-600 hover:bg-red-700'}`}
                >
                  {busy ? t('working') : preview.decision === 'accepted' ? t('confirmAccept', ctx) : t('confirmReject', rejectCtx)}
                </button>
                {failed && <p className="mt-2 text-xs text-red-600" data-testid="terms-decide-fail">{failed === 'avv' ? t('avvFirst') : failed === 'unpublished' ? t('notPublished') : t('fail')}</p>}
              </div>
            )}
            <article
              className="prose prose-sm mt-6 max-w-none text-sm leading-relaxed [&_h1]:text-xl [&_h1]:font-semibold [&_h2]:mt-6 [&_h2]:text-base [&_h2]:font-semibold [&_h3]:mt-4 [&_h3]:font-semibold [&_p]:mt-2 [&_ul]:mt-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mt-2 [&_ol]:list-decimal [&_ol]:pl-5"
              data-testid="terms-decide-body"
              lang={v.language}
            >
              <ReactMarkdown>{v.content_md}</ReactMarkdown>
            </article>
          </>
        )}
        <LegalFooter className="mt-8" />
      </div>
    </div>
  );
}
